import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, FlexBox, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { Club, ClubType } from '@fable/types'
import { getAuthorNames, getBookDiscount } from '@fable/utils'
import { analytics } from 'segment_analytics'
import { FORM_TYPES } from 'app/auth/constants'
import { BookGraphic } from './BookGraphic'
import { ClubJoinedModal } from './ClubJoinedModal'
import { AuthForm } from 'app/auth/components/auth_form/AuthForm'
import { clubAuthKey, clubPromoCode } from 'state'
import { promoCodes } from '../../../../../constants'
import { useActions, useCart, useTypedSelector } from 'hooks'
import { routes } from 'app/store/routes'
import { useSelector } from '@xstate/react'

export const ClubBookPromoModal = ({
  club,
  clubType,
}: {
  club: Club | null
  clubType: ClubType | null
}) => {
  const [skipped, setSkipped] = useState(false)
  const [buying, setBuying] = useState(false)
  const { isAuthenticated } = useTypedSelector(({ auth }) => auth)
  const { closeModal } = useActions()
  const { service } = useCart()
  const cartCtx = useSelector(service, (state) => state.context)
  const navigate = useNavigate()
  const { colors, mediaQueries } = useTheme()
  const book = club?.current_book
  const { discount, percentageOff } = getBookDiscount({
    price: book?.price_usd as string,
  })

  if (buying) {
    return (
      <AuthForm
        className={css`
          margin: 0 auto;
          background-color: transparent;
          ${mediaQueries.mobileL} {
            width: 496px;
          }
        `}
        type={FORM_TYPES.SIGN_UP}
      />
    )
  }

  if (skipped) {
    return (
      <ClubJoinedModal club={club as Club} clubType={clubType as ClubType} />
    )
  }

  const handleOnClick = () => {
    analytics.events.ebookPromoUpsell.buyCta()
    const code = promoCodes.readMore
    localStorage.setItem(clubPromoCode, code)
    const cartExists = !!cartCtx.cart && !!cartCtx.id

    service.send({
      type: cartExists ? 'ADD_ITEM' : 'CREATE',
      data: {
        product_id: book?.id as string,
        product_type: 'book',
        quantity: 1,
      },
    })

    if (isAuthenticated) {
      const url = `${routes.checkout}/${book?.isbn}?code=${code}`
      navigate(url)
      closeModal()
      return
    }

    localStorage.setItem(clubAuthKey, 'true')
    setBuying(true)
  }

  return (
    <FlexBox
      className={css`
        padding: 32px 16px 72px;
        width: 100%;
        text-align: center;
        ${mediaQueries.tablet} {
          padding: 32px 48px 72px;
          width: 496px;
        }
      `}
      flexDirection="column"
      alignItems="center"
    >
      <Text
        className={css`
          text-transform: uppercase;
        `}
        type="label"
        sizing="L"
        color={colors.uiGreenDark}
      >
        Today only
      </Text>
      <Text
        className={css`
          margin: 12px 0;
          max-width: 260px;
          ${mediaQueries.mobileL} {
            max-width: 394px;
          }
        `}
        type="header"
        sizing={{ base: 'XS', mobileL: 'S' }}
        color={colors.uiBlack}
      >
        Get $5 off when you buy your club’s interactive ebook today!
      </Text>
      <Text
        className={css`
          max-width: 344px;
          ${mediaQueries.mobileL} {
            max-width: 390px;
          }
        `}
        type="body"
        sizing={{ base: 'M', mobileL: 'L' }}
        color={colors.uiGraphiteLight}
      >
        You'll unlock premium social reading features when you buy and read your
        club’s ebook on Fable.
      </Text>
      <FlexBox
        className={css`
          margin: 16px 0 8px;
          width: 100%;
          border-radius: 18px;
          overflow: hidden;
        `}
        flexDirection="column"
      >
        <FlexBox
          className={css`
            padding-top: 32px;
            background-color: ${book?.background_color};
            justify-content: center;
            position: relative;
            overflow: hidden;
          `}
        >
          <BookGraphic color={book?.background_color} />
          <img
            className={css`
              margin-bottom: -30px;
              width: min(44.048vw, 185px);
              height: min(64.912vw, 285px);
              background-color: ${colors.uiSilverGrey};
              border-radius: 8px;
              overflow: hidden;
              position: relative;
              z-index: 2;
            `}
            src={book?.cover_image}
            alt={`${book?.title} cover`}
          />
          <BookGraphic color={book?.background_color} right />
        </FlexBox>
        <div
          className={css`
            padding: 12px;
            background-color: ${colors.uiPearlUltraLight};
            border-top: 4px solid ${colors.uiWhite};
            text-align: left;
          `}
        >
          <FlexBox justifyContent="space-between" alignItems="center" gap="8px">
            <Text
              type="body"
              sizing={{ base: 'M', mobileL: 'L' }}
              color={colors.uiGraphite}
              truncate={1}
            >
              {book?.title}
            </Text>
            <FlexBox
              className={css`
                gap: 12px;
                ${mediaQueries.mobileL} {
                  gap: 25px;
                }
              `}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Text
                className={css`
                  text-decoration: line-through;
                `}
                type="body"
                sizing={{ base: 'M', mobileL: 'L' }}
                color={colors.uiGraphite}
              >
                ${book?.price_usd}
              </Text>
              <Text
                type="subhead"
                sizing={{ base: 'XS', mobileL: 'S' }}
                color={colors.uiRose}
              >
                {discount}
              </Text>
            </FlexBox>
          </FlexBox>
          <FlexBox justifyContent="space-between" alignItems="center" gap="8px">
            <Text
              type="body"
              sizing={{ base: 'XS', mobileL: 'S' }}
              color={colors.uiPewterGrey}
              truncate={1}
            >
              By {getAuthorNames(book?.authors)}
            </Text>
            <Text type="label" sizing="L" color={colors.uiRose}>
              {percentageOff} off
            </Text>
          </FlexBox>
        </div>
      </FlexBox>
      <FlexBox
        className={css`
          width: 100%;
        `}
        flexDirection="column"
        gap="8px"
      >
        <Button width="100%" onClick={handleOnClick}>
          Buy the interactive ebook
        </Button>
        <Button
          width="100%"
          outline
          onClick={() => {
            analytics.events.ebookPromoUpsell.skipCta()
            setSkipped(true)
          }}
        >
          Skip
        </Button>
      </FlexBox>
    </FlexBox>
  )
}
